import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import {
  signInWithEmail,
  signUpWithEmail,
  resetPassword as resetPasswordService,
  updatePassword as updatePasswordService,
  signOut,
  getSession,
} from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    // If the URL carries auth params (PKCE code or implicit tokens), Supabase is about to
    // exchange them for a session. Stay in the loading state until the auth flow completes
    // so we don't flash the login page before the session is established.
    const hasAuthInUrl =
      window.location.search.includes('code=') ||
      window.location.hash.includes('access_token=');

    if (!hasAuthInUrl) {
      checkSession();
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session?.user?.email);

        // Handle PASSWORD_RECOVERY event - user clicked reset link from email
        if (event === 'PASSWORD_RECOVERY') {
          console.log('Password recovery mode activated');
          setIsPasswordRecovery(true);
        }

        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata?.full_name || session.user.email,
            avatar: session.user.user_metadata?.avatar_url,
            role: session.user.app_metadata?.role || 'locate2u',
          });
        } else {
          setUser(null);
        }

        // Don't clear loading on the initial null-session event when we know Supabase
        // is still processing a URL token — wait for the post-exchange event instead.
        if (!hasAuthInUrl || event !== 'INITIAL_SESSION' || session?.user) {
          setLoading(false);
        }
      }
    );

    // Failsafe: if the URL auth exchange never resolves (invalid/expired token), don't
    // leave the user stuck on a loading spinner forever.
    const timeoutId = hasAuthInUrl ? setTimeout(() => setLoading(false), 3000) : null;

    return () => {
      subscription?.unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  async function checkSession() {
    try {
      const session = await getSession();
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.full_name || session.user.email,
          avatar: session.user.user_metadata?.avatar_url,
          role: session.user.app_metadata?.role || 'locate2u',
        });
      }
    } catch (err) {
      console.error('Error checking session:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loginWithEmail(email, password) {
    try {
      setError(null);
      await signInWithEmail(email, password);
    } catch (err) {
      console.error('Error logging in with email:', err);
      setError(err.message);
      throw err;
    }
  }

  async function signUp(email, password) {
    try {
      setError(null);
      return await signUpWithEmail(email, password);
    } catch (err) {
      console.error('Error signing up:', err);
      setError(err.message);
      throw err;
    }
  }

  async function resetPassword(email) {
    try {
      setError(null);
      await resetPasswordService(email);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message);
      throw err;
    }
  }

  async function updatePassword(newPassword) {
    try {
      setError(null);
      await updatePasswordService(newPassword);
    } catch (err) {
      console.error('Error updating password:', err);
      setError(err.message);
      throw err;
    }
  }

  // Clear password recovery mode (used when user navigates away)
  function clearPasswordRecovery() {
    setIsPasswordRecovery(false);
  }

  async function logout() {
    try {
      setError(null);
      await signOut();
      setUser(null);
    } catch (err) {
      console.error('Error logging out:', err);
      setError(err.message);
    }
  }

  const value = {
    user,
    loading,
    error,
    isPasswordRecovery,
    loginWithEmail,
    signUp,
    resetPassword,
    updatePassword,
    clearPasswordRecovery,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
